export interface ConversationSummary {
  id: string;
  started_at: number;
  updated_at: number;
  title: string;
  chapter_index: number;
  project_badge: string | null;
  project_name: string | null;
}

export interface ProjectInfo {
  badge: string;
  name: string;
}

export interface AnalyzeProjectResult {
  ok: boolean;
  projectInfo?: ProjectInfo;
  error?: string;
}

export interface AskOption {
  label: string;
  description?: string;
}

/** 章节提示词要求先向用户确认申办方政策时，模型这一轮产出的结构化问题。 */
export interface AskQuestion {
  id: string;
  header?: string;
  question: string;
  options?: AskOption[];
  multiSelect?: boolean;
  allowFreeText?: boolean;
}

export interface ConversationMessage {
  role: "user" | "assistant";
  /** 助手消息：本轮的正式答复。 */
  content: string;
  created_at: number;
  questions?: AskQuestion[];
  /** 助手消息的过程信息（模型边做边说的进度说明）；此功能上线前的历史消息为 null。 */
  process?: string | null;
  /** 该轮是否把章节正文写进了方案文档。 */
  chapter_written?: number;
}

export async function listConversations(): Promise<ConversationSummary[]> {
  const res = await fetch("/api/conversations");
  if (!res.ok) throw new Error("获取历史对话失败");
  const data = (await res.json()) as { conversations: ConversationSummary[] };
  return data.conversations;
}

export async function getConversation(
  id: string
): Promise<{
  conversation: ConversationSummary;
  messages: ConversationMessage[];
  awaitingAnswers: boolean;
  completedChapters: string[];
}> {
  const res = await fetch(`/api/conversations/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error("获取对话详情失败");
  return (await res.json()) as {
    conversation: ConversationSummary;
    messages: ConversationMessage[];
    awaitingAnswers: boolean;
    completedChapters: string[];
  };
}

export type ExportFormat = "docx" | "pdf" | "markdown";

/** 导出已完成章节。PDF 走浏览器打印（新开一个打印样式页面），其余直接下载文件。 */
export function exportConversation(id: string, format: ExportFormat): void {
  const url = `/api/conversations/${encodeURIComponent(id)}/export?format=${format}`;
  if (format === "pdf") {
    window.open(url, "_blank", "noopener");
    return;
  }
  const a = window.document.createElement("a");
  a.href = url;
  a.rel = "noopener";
  window.document.body.appendChild(a);
  a.click();
  a.remove();
}

export async function deleteConversation(id: string): Promise<void> {
  const res = await fetch(`/api/conversations/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!res.ok) throw new Error((await res.json().catch(() => null))?.error ?? "删除对话失败");
}

export async function deleteAllConversations(): Promise<void> {
  const res = await fetch("/api/conversations", { method: "DELETE" });
  if (!res.ok) throw new Error((await res.json().catch(() => null))?.error ?? "清空历史对话失败");
}

export async function startThread(): Promise<string> {
  const res = await fetch("/api/chat/thread", { method: "POST" });
  if (!res.ok) throw new Error((await res.json().catch(() => null))?.error ?? "启动会话失败");
  const data = (await res.json()) as { threadId: string };
  return data.threadId;
}

export async function extractPdfText(file: File): Promise<string> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch("/api/chat/extract-pdf", { method: "POST", body: form });
  if (!res.ok) throw new Error((await res.json().catch(() => null))?.error ?? "解析 PDF 失败");
  const data = (await res.json()) as { text: string };
  return data.text;
}

export async function extractDocxText(file: File): Promise<string> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch("/api/chat/extract-docx", { method: "POST", body: form });
  if (!res.ok) throw new Error((await res.json().catch(() => null))?.error ?? "解析 Word 文档失败");
  const data = (await res.json()) as { text: string };
  return data.text;
}

export interface StreamMessageResult {
  /** 本轮的正式答复。 */
  text: string;
  /** 本轮的过程信息（进度说明），与答复分开展示。 */
  process: string;
  /** 本轮是否把章节正文写进了方案文档。 */
  chapterWritten: boolean;
  /** 本轮实际撰写的章节下标；服务端在意图判定完成后才确定。 */
  chapterIndex?: number;
  chapterAdvanced: boolean;
  /** 本轮是在修订当前章节，而不是新写一章。 */
  revising: boolean;
  /** 本轮模型提出的待回答问题；非空表示这是一轮提问、没有产出正文。 */
  questions?: AskQuestion[];
  /** 真正已产出正文的章节编号（本轮结束后的最新值）。 */
  completedChapters?: string[];
  projectAnalysis?: ProjectAnalysisState;
}

/** 生成过程中的进度回调：章节意图判定完成后，服务端才知道这一轮在写哪一章。 */
export interface TurnProgress {
  chapterIndex?: number;
  revising: boolean;
  /** 截至目前的过程信息，生成过程中持续更新。 */
  process: string;
}

export interface ProjectAnalysisState {
  status: "pending" | "done" | "failed";
  info?: ProjectInfo;
  error?: string;
}

interface TurnStatusResponse {
  status: "idle" | "streaming" | "done" | "error" | "deleted";
  text?: string;
  process?: string;
  chapterWritten?: boolean;
  chapterIndex?: number;
  chapterAdvanced?: boolean;
  revising?: boolean;
  questions?: AskQuestion[];
  completedChapters?: string[];
  error?: string;
  projectAnalysis?: ProjectAnalysisState;
}

const POLL_INTERVAL_MS = 500;

async function fetchTurnStatus(threadId: string): Promise<TurnStatusResponse> {
  const res = await fetch(`/api/chat/message?threadId=${encodeURIComponent(threadId)}`);
  if (!res.ok) {
    throw new Error((await res.json().catch(() => null))?.error ?? "获取生成状态失败");
  }
  return (await res.json()) as TurnStatusResponse;
}

/** 轮询直到本轮生成结束（服务端已经把生成过程和 HTTP 连接解耦，所以即使这次轮询中途
 *  页面被刷新，重新调用本函数也能从服务端拿到最新进度，不会丢失已经生成的内容）。 */
export async function watchTurn(
  threadId: string,
  onProgress?: (progress: TurnProgress) => void
): Promise<StreamMessageResult> {
  let lastChapterIndex: number | undefined;
  let lastProcess = "";
  while (true) {
    const s = await fetchTurnStatus(threadId);
    if (s.status === "deleted") throw new Error(s.error ?? "对话已被清理");
    if (s.status === "idle") throw new Error("未找到生成中的对话，请重试");
    const process = s.process ?? "";
    if ((s.chapterIndex !== undefined && s.chapterIndex !== lastChapterIndex) || process !== lastProcess) {
      if (s.chapterIndex !== undefined) lastChapterIndex = s.chapterIndex;
      lastProcess = process;
      onProgress?.({ chapterIndex: lastChapterIndex, revising: !!s.revising, process });
    }
    if (s.status === "error") throw new Error(s.error ?? "生成失败");
    if (s.status === "done") {
      return {
        text: s.text ?? "",
        process: s.process ?? "",
        chapterWritten: !!s.chapterWritten,
        chapterIndex: s.chapterIndex,
        chapterAdvanced: !!s.chapterAdvanced,
        revising: !!s.revising,
        questions: s.questions,
        completedChapters: s.completedChapters,
        projectAnalysis: s.projectAnalysis,
      };
    }
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }
}

/** 发起一轮新的生成（仅负责触发，不等待完成），再用 watchTurn 轮询进度。 */
export async function streamMessage(
  threadId: string,
  text: string,
  onProgress?: (progress: TurnProgress) => void
): Promise<StreamMessageResult> {
  const res = await fetch("/api/chat/message", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ threadId, text }),
  });
  if (!res.ok) {
    throw new Error((await res.json().catch(() => null))?.error ?? "生成失败");
  }
  return watchTurn(threadId, onProgress);
}

/** 轮询等待首轮项目信息提取完成（该分析在服务端于首轮回复生成完成后自动触发）。 */
export async function watchProjectAnalysis(threadId: string): Promise<AnalyzeProjectResult> {
  while (true) {
    const s = await fetchTurnStatus(threadId);
    if (s.status === "deleted") return { ok: false, error: s.error ?? "项目信息分析失败" };
    const pa = s.projectAnalysis;
    if (pa?.status === "done" && pa.info) return { ok: true, projectInfo: pa.info };
    if (pa?.status === "failed") return { ok: false, error: pa.error ?? "项目信息分析失败" };
    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
  }
}

/** 页面加载/刷新时用于探测某个会话当前的服务端生成状态，以便恢复轮询而不是重新发起。 */
export async function getTurnStatus(threadId: string): Promise<TurnStatusResponse> {
  return fetchTurnStatus(threadId);
}
